// src/api/auth/auth.ts
import { api, clearSession, setSession } from "../api";
import { refreshSession } from "../token-storage";
import type { AuthResponse } from "./auth.types";

export const authApi = {
    checkEmailAvailable: async (
        email: string,
        invitationCode?: string,
    ): Promise<{ available: boolean; reason?: string }> => {
        const { data } = await api.get<{ available: boolean; reason?: string }>(
            "/auth/check-email",
            {
                params: {
                    email,
                    ...(invitationCode
                        ? { invitation_code: invitationCode }
                        : {}),
                },
            },
        );
        return data;
    },

    signupEmail: async (email: string, invitationCode?: string) => {
        return api.post("/auth/signup-email", {
            email,
            invitation_code: invitationCode,
            mode: "signup",
        });
    },

    resend_otp: async (data: { email: string; invitation_code?: string }) => {
        return api.post("/auth/resend-otp", data);
    },

    signinEmail: async (email: string) => {
        return api.post("/auth/signup-email", { email, mode: "login" });
    },

    verifyOtp: async (
        email: string,
        token: string,
        type: "email" | "sms" = "email",
        invitationCode?: string,
    ) => {
        const { data } = await api.post<AuthResponse>("/auth/verify-otp", {
            email,
            otp_code: token,
            type,
            invitation_code: invitationCode,
        });

        // Only set session if tokens are present (not onboarding)
        if (data.access_token && data.refresh_token) {
            setSession(data.access_token, data.refresh_token, {
                ...(typeof data.expires_in === "number"
                    ? { expires_in: data.expires_in }
                    : {}),
                emit: false,
            });
        }

        return data;
    },

    signinPassword: async (email: string, password: string) => {
        const { data } = await api.post<AuthResponse>("/auth/signin-password", {
            email,
            password,
        });

        setSession(data.access_token, data.refresh_token, {
            ...(typeof data.expires_in === "number"
                ? { expires_in: data.expires_in }
                : {}),
            emit: false, // signIn() in hooks.ts handles profile loading via syncSession()
        });

        return data;
    },

    signOut: async (scope: "local" | "global" = "local") => {
        // Clear local session immediately so the UI redirects to /login
        // without waiting for the backend. The server call is best-effort —
        // if it hangs or fails (slow network, CORS timeout, cold start),
        // the user should never be stuck staring at a dead chat screen.
        clearSession();

        try {
            await api.post("/auth/signout", { scope });
        } catch {
            // Server-side session cleanup failed — local tokens are already
            // gone so the user is effectively logged out. The server session
            // will expire on its own (Supabase JWT TTL).
        }
    },

    // ✅ single source refresh
    refreshToken: async () => {
        const refreshed = await refreshSession();
        if (!refreshed) throw new Error("Refresh failed.");

        // Don't emit auth:change here — callers (boot, interceptor) control emission
        // to avoid cascading re-sync loops.
        setSession(refreshed.access_token, refreshed.refresh_token, {
            ...(typeof refreshed.expires_in === "number"
                ? { expires_in: refreshed.expires_in }
                : {}),
            ...(typeof refreshed.expires_at === "number"
                ? { expires_at: refreshed.expires_at }
                : {}),
            emit: false,
        });

        return refreshed;
    },

    setPassword: async (password: string) => {
        const { data } = await api.post("/auth/set-password", { password });
        return data;
    },

    updatePassword: async (currentPassword: string, newPassword: string) => {
        const { data } = await api.post("/auth/update-password", {
            current_password: currentPassword,
            new_password: newPassword,
        });
        return data;
    },

    forgotPassword: async (email: string, redirectTo?: string) => {
        const { data } = await api.post("/auth/forgot-password", {
            email,
            redirect_to: redirectTo,
        });
        return data;
    },

    verifyResetCode: async (email: string, code: string) => {
        const { data } = await api.post("/auth/verify-reset-code", {
            email,
            code,
        });
        return data;
    },

    resetPasswordWithCode: async (email: string, code: string, newPassword: string) => {
        const { data } = await api.post("/auth/reset-password-with-code", {
            email,
            code,
            new_password: newPassword,
        });
        return data;
    },

    resetPassword: async (accessToken: string, newPassword: string) => {
        const { data } = await api.post("/auth/reset-password", {
            access_token: accessToken,
            new_password: newPassword,
        });
        return data;
    },

    signupPassword: async (email: string, password: string, invitationCode: string) => {
        const { data } = await api.post<AuthResponse>("/auth/signup-password", {
            email,
            password,
            invitation_code: invitationCode,
        });

        setSession(data.access_token, data.refresh_token, {
            ...(typeof data.expires_in === "number"
                ? { expires_in: data.expires_in }
                : {}),
            emit: false, // Don't emit auth:change during signup - syncSession() will handle profile loading
        });

        return data;
    },

    sendSmsCode: async (phone: string) => {
        const { data } = await api.post("/auth/sms/send-code", { phone });
        return data;
    },

    checkSmsCode: async (phone: string, code: string) => {
        const { data } = await api.post("/auth/sms/check-code", {
            phone,
            code,
        });
        return data;
    },

    requestEmailChangeOtp: async (newEmail: string) => {
        const { data } = await api.post<{ message: string }>(
            "/email-change/request",
            { new_email: newEmail },
        );
        return data;
    },

    verifyEmailChangeOtp: async (newEmail: string, code: string) => {
        const { data } = await api.post<{ message: string }>(
            "/email-change/verify",
            { new_email: newEmail, otp_code: code },
        );
        return data;
    },
};
